import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Waves, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      const dest = location.state?.from?.pathname || "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative bg-secondary overflow-hidden">
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/job_spa-report-hub/artifacts/i4l1k62f_542DB015-7DA7-4F8F-A41A-8344510A2CB6_1_105_c.jpeg"
          alt="Spa jacuzzi extérieur"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/70 to-slate-950/60" />
        <div className="absolute inset-0 grid-pattern-dense opacity-10" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-md bg-white/10 backdrop-blur-sm">
              <Waves className="h-5 w-5" />
            </span>
            <span className="font-display font-black text-lg">DoctoSpa</span>
          </Link>
          <div>
            <div className="eyebrow text-white/60">Bienvenue</div>
            <h2 className="mt-3 font-display text-4xl font-black tracking-tighter">
              Le diagnostic technique de votre spa,<br />en 2 minutes.
            </h2>
            <p className="mt-4 text-white/70 text-sm max-w-md">
              Retrouvez l'historique de vos diagnostics et accédez à vos rapports détaillés.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md p-8 border-black/10 shadow-sm">
          <div className="eyebrow">Connexion</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Content de vous revoir</h1>
          <p className="mt-2 text-sm text-muted-foreground">Entrez vos identifiants pour continuer.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                placeholder="vous@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-center text-muted-foreground">
            Pas encore de compte ?{" "}
            <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="link-to-register">
              Créer un compte
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
